import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
  type IHeaderComp,
  type IHeaderParams,
  type IRowNode,
  type ValueFormatterParams,
  type ValueGetterParams,
  type ValueSetterParams,
} from 'ag-grid-community';
import { awaitJob, type JobProgress } from '../../lib/await-job.js';
import { readUnitUpdateOutcome } from '../../lib/unit-update-result.js';
import { kauflandBackendUrl, kauflandFrontendUrl, varioArticleUrl } from '../../lib/kaufland-links.js';
import {
  loadActivatedStorefronts,
  resolveInitialStorefront,
  storefrontFilterOptions,
  type ActivatedStorefront,
} from '../../lib/storefront-selection.js';
import { currentVarioSalesChannelId } from '../../lib/vario-route-context.js';
import { showToast, openModal, createKfDrawer, KF_ICON, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND, kfFlagSvg } from '@vario-connect/frontend-design';
import { openArticleDialog } from './offers-article-dialog.js';

interface OffersCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface SummaryRow {
  tenant_id: string;
  storefront: string;
  status: string;
  alive: number;
  tombstoned: number;
  total: number;
}

interface UnitRow {
  storefront: string;
  id_unit: string;
  id_offer: string | null;
  id_product: number | null;
  ean: string | null;
  title: string | null;
  status: string | null;
  price_cents: number | null;
  stock: number | null;
  minimum_price_cents: number | null;
  handling_time: number | null;
  updated_at: string;
  /** Wann diese Row zuletzt von Kaufland geholt wurde (jeder refresh_units-Lauf setzt das,
   *  egal ob sich Felder geändert haben). UI zeigt das als „Aktualisiert"-Spalte, damit
   *  der User sieht, dass die Daten frisch sind — auch bei unverändertem Listing. */
  fetched_at: string | null;
  deleted_at: string | null;
  /** From vario.article_mappings (LEFT JOIN). null = noch nicht zugewiesen. */
  vario_article_id: string | null;
  vario_article_number: string | null;
  /** Aktueller Vario-Bestand (stockAvailable). NULL = noch nie refresht oder kein Mapping. */
  vario_stock: number | null;
  vario_stock_fetched_at: string | null;
  /** Min-Bestand für die Auffüllen-Logik. NULL = Feature aus. Trigger: kaufland_stock ≤ min ODER vario < min. */
  min_bestand: number | null;
  /** Max-Bestand für die Auffüllen-Logik (Zielwert beim Push). NULL = Feature aus. */
  max_bestand: number | null;
  /** From vario.article_match_suggestions (LEFT JOIN). NON-NULL bedeutet: Vario-Artikel existiert
   *  mit passender EAN/SKU, ist aber noch nicht via Mapping übernommen. Bei (vario_article_id == null
   *  && suggested_vario_article_id != null) → Zeile blau hervorheben. */
  suggested_vario_article_id: string | null;
  suggested_vario_article_number: string | null;
  /** Aktive Kaufland-Versandgruppe (`id_shipping_group`). null = Default. Aus der Spalte
   *  in kaufland.units, die der refresh_units-Job mitschreibt. */
  id_shipping_group: string | null;
  /** MwSt-Kennzeichen am Listing (Kaufland-Pflichtfeld: `standard_rate`, `reduced_rate_1`,
   *  `zero_rate`, ...). Klartext-/Prozent-Anzeige via `kaufland.vat_indicators`-Cache. */
  vat_indicator: string | null;
  /** Vario-Artikelart (WITH_STOCK | WITHOUT_STOCK | SERVICE_ARTICLE). null = noch nicht erfasst.
   *  WITHOUT_STOCK/SERVICE_ARTICLE = bestandslos → „Vario"-Spalte zeigt „kein Bestand", Min/Max
   *  füllt auf Max ohne Vario-Deckel. */
  vario_product_type: string | null;
}

/** Bestandslose Vario-Artikelarten (kein Lagerbestand) — Spiegel des Workers. */
const STOCKLESS_PRODUCT_TYPES = new Set(['WITHOUT_STOCK', 'SERVICE_ARTICLE']);
function isStocklessRow(row: UnitRow | undefined): boolean {
  return !!row && row.vario_product_type !== null && STOCKLESS_PRODUCT_TYPES.has(row.vario_product_type);
}

interface ShippingGroupRow {
  id_shipping_group: string;
  name: string | null;
}

type EditableField = 'listing_price' | 'amount' | 'status' | 'minimum_price' | 'handling_time';

interface PendingEdit {
  storefront: string;
  idUnit: string;
  fields: Partial<{
    listingPriceCents: number;
    amount: number;
    status: 'active' | 'paused';
    minimumPriceCents: number;
    handlingTime: number;
  }>;
}

const POLL_INTERVAL_MS = 3000;
// Block-Größe = größte Page-Size im paginationPageSizeSelector unten.
// Sonst läuft SelectAllHeader.forEachNode nur über teilweise geladene Pages
// (z. B. bei Page-Size 200 und Block-Size 100 zeigt der Counter 100 statt 200,
//  weil der zweite Block noch nicht im Cache ist).
const GRID_BLOCK_SIZE = 200;     // Rows per server fetch — must stay ≤ PostgREST max_rows (1000 default).
const GRID_CACHE_BLOCKS = 10;    // ~2 000 rows in client memory while scrolling.

/**
 * Enge Zahlenspalte (Preise, Bestaende). Das Theme gibt `--ag-cell-horizontal-padding: 20px`
 * vor, also 40px pro Spalte — bei vier Bestandsspalten allein 160px nur fuer Luft. Fuer
 * rechtsbuendige Zahlen genuegen 8px je Seite. `tabular-nums` haelt die Ziffern gleich breit,
 * damit die Spalte bei jedem Wert gleich aussieht.
 */
const NUM_CELL = {
  fontVariantNumeric: 'tabular-nums',
  padding: '0 8px',
} as const;

/**
 * Rechtsbuendigkeit kommt aus der Theme-Konvention `r` (Zelle) und `hr` (Header):
 * `.ag-cell.r{justify-content:flex-end}`. AG-Grid-Zellen sind Flex-Container, ein blosses
 * `text-align:right` im cellStyle greift dort nicht zuverlaessig — genau deshalb gibt es
 * die Klassen. `kl-th-num`/`kl-th-mono` steuern ausschliesslich das Padding.
 */
const NUM_CELL_CLASS = 'r';

/** Monospace-Spalte (SKU, Vario-SKU) mit ebenfalls reduziertem Padding.
 *  Schriftart und -groesse kommen aus `--kl-mono-cell-*` in kf-page.css, damit die Bestellungen
 *  (Order-ID, Belegnummern, Tracking) dieselben Werte nutzen statt eigener. Die Fallbacks halten
 *  die Spalte lesbar, falls das Grid einmal ausserhalb von `.kf-page` gerendert wird — die
 *  9px sind die Basis der 132px-Breitenrechnung fuer 20 Zeichen SKU.
 *  Das Padding bleibt bewusst hier: Es geht in dieselbe Breitenrechnung ein. */
const MONO_CELL = {
  fontFamily: 'var(--kl-mono-cell-ff, ui-monospace, SFMono-Regular, Menlo, monospace)',
  fontSize: 'var(--kl-mono-cell-fs, 9px)',
  padding: '0 12px',
} as const;

/**
 * Breite der Bestandsspalten: Platz fuer sieben Ziffern.
 * 7 × 6,9px (Outfit-Ziffer ≈ 0,6 × 11,5px) = 48px, dazu 2 × 8px Padding, aufgerundet auf 68px.
 * Die Header "Best.", "Vario", "Min" und "Max" bleiben darunter, bestimmen die Breite also nicht.
 */
const NUM_COL = {
  width: 68,
  minWidth: 68,
  cellClass: NUM_CELL_CLASS,
  headerClass: 'hr kl-th-num',
} as const;

/**
 * Nur Ausrichtung, ohne Breite. Fuer Zahlenspalten, die breiter bleiben als die
 * Bestandsspalten (Preise). Ohne `hr` stuende der Header linksbuendig ueber rechtsbuendigen
 * Werten — genau das fiel nach der Breiten-Umstellung auf.
 */
const NUM_HEADER = { cellClass: NUM_CELL_CLASS, headerClass: 'hr kl-th-num' } as const;

/** Snapshot of the active filter state. Passed by-reference to the datasource. */
interface FilterState {
  storefront: string | null;
  status: string | null;
  /** null = alle, 'mapped' = nur mit vario_article_id, 'unmapped' = nur ohne,
   *  'exists-unmapped' = ohne mapping, aber `suggested_vario_article_id` non-null
   *  (= Vario hat den Artikel via EAN/SKU bereits, nur das Mapping fehlt). */
  mapping: 'mapped' | 'unmapped' | 'exists-unmapped' | null;
  search: string;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  inactive: 'Inaktiv',
  paused: 'Pausiert',
  deleted: 'Gelöscht',
  unknown: 'Unbekannt',
};

interface OffersSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-App-Tab `/#/offers`
 *  (settings.sales-channels.{appId}.tabs). Rendert die Angebotsübersicht mit
 *  eigenem Kopf (wie Bestellungen/Log) über die volle iframe-Höhe. */
export function renderOffers(ctx: OffersSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderOffersTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}

export async function renderOffersTab(host: HTMLElement, ctx: OffersCtx, opts: { standalone?: boolean } = {}) {
  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Angebote</div><h1>Angebotsübersicht</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Kaufland verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-offers-refresh" title="Komplettabgleich: Kaufland-Listings + Vario-Bestand neu laden und Bestand-Deltas an Kaufland pushen (sofern aktiviert)">
            <span class="v-icon fa-regular fa-rotate-right" id="kl-offers-refresh-icon"></span>Aktualisieren
          </button>
        </div>
      </header>`
    : '';
  const refreshInFilterbar = standalone
    ? ''
    : `<button type="button" class="btn btn-primary" id="kl-offers-refresh" title="Komplettabgleich: Kaufland-Listings + Vario-Bestand neu laden und Bestand-Deltas an Kaufland pushen (sofern aktiviert)">
        <span class="v-icon fa-regular fa-rotate-right" id="kl-offers-refresh-icon"></span>Aktualisieren
      </button>`;
  const filterbar = `<div class="filterbar">
      <div class="select"><select id="kl-offers-storefront" title="Storefront filtern"></select></div>
      <div class="select"><select id="kl-offers-status" title="Status filtern">
        <option value="">Alle Status</option>
        <option value="active">Aktiv</option>
        <option value="paused">Pausiert</option>
        <option value="inactive">Inaktiv</option>
        <option value="deleted">Gelöscht</option>
      </select></div>
      <div class="select"><select id="kl-offers-mapping" title="Vario-Mapping filtern">
        <option value="">Alle Mappings</option>
        <option value="unmapped">Nicht zugeordnet</option>
        <option value="exists-unmapped">In Vario vorhanden, nicht zugeordnet</option>
        <option value="mapped">Zugeordnet</option>
      </select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="kl-offers-search" type="search" placeholder="EAN, SKU, ID, Titel suchen…" autocomplete="off" /><button type="button" class="clearbtn" id="kl-offers-search-clear" aria-label="Suche leeren">${KF_ICON.close}</button></div>
      ${refreshInFilterbar}
      <div class="kl-legend-wrap">
        <button type="button" class="icon-btn" id="kl-offers-legend-btn" title="Legende anzeigen" aria-label="Legende anzeigen" aria-expanded="false">${KF_ICON.help}</button>
        <div id="kl-offers-legend-popover" class="kl-legend-popover" hidden>
          <div class="kl-legend-title">Zeilen-Farben</div>
          <ul class="kl-legend-list">
            <li><span class="kl-legend-swatch kl-legend-swatch-red"></span><span><strong>Hellrot</strong>: nicht zugeordnet</span></li>
            <li><span class="kl-legend-swatch kl-legend-swatch-blue"></span><span><strong>Hellblau</strong>: in Vario vorhanden, aber nicht zugeordnet</span></li>
            <li><span class="kl-legend-swatch kl-legend-swatch-white"></span><span><strong>Weiß</strong>: zugeordnet</span></li>
          </ul>
          <div class="kl-legend-title kl-legend-title--sub">Synchronisation</div>
          <ul class="kl-legend-bullets">
            <li>Bestände werden alle 5 Minuten abgeglichen</li>
            <li>Bestellungen werden alle 10 Minuten abgerufen</li>
            <li>Komplettabgleich erfolgt alle zwei Stunden</li>
            <li>Preise werden dabei von Kaufland gelesen, nicht dorthin übertragen</li>
            <li>Im Preisfeld geänderte Werte gehen beim Speichern sofort an Kaufland</li>
            <li>Manueller Sync jederzeit möglich</li>
          </ul>
        </div>
      </div>
    </div>`;
  const gridWrapStyle = standalone ? 'flex:1;min-height:0;' : 'height:min(74vh,900px);min-height:480px;flex:none;';
  // `kl-grid-compact`: eine Stufe kleinere Zellschrift, weil dieses Grid deutlich mehr Spalten
  // fuehrt als die uebrigen Tabellen. Definiert in kf-page.css, damit Angebote und
  // Bestellungen denselben Wert teilen.
  const card = `<div class="card"><div class="grid-wrap" style="${gridWrapStyle}"><div id="kl-grid" class="ag-theme-vario ag-theme-quartz kl-grid-compact" style="height:100%;width:100%;"></div></div></div>`;
  const body = `<div id="kl-offers-resume-banner" hidden></div>${filterbar}<div id="kl-offers-toolbar"></div>${card}`;
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}${body}</main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${body}</div>`;

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  const drawer = createKfDrawer(root);
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }

  const filters: FilterState = {
    storefront: null,
    status: null,
    mapping: null,
    search: '',
  };
  const pending = new Map<string, PendingEdit>();
  let pollTimer: number | null = null;
  let gridApi: GridApi<UnitRow> | null = null;
  /**
   * Im AG-Grid-`context` referenziert, damit `SelectAllHeader` (Klasse außerhalb des
   * Closure-Scopes) die Toolbar nach Selection-Toggle neu rendern kann.
   */
  const gridContext: OffersGridContext = {
    refreshToolbar: () => renderToolbar(host, pending, ctx, gridApi, filters, () => void refreshAll()),
    shippingGroups: new Map(),
    vatIndicators: new Map(),
  };

  await Promise.all([loadShippingGroups(ctx, gridContext), loadVatIndicators(ctx, gridContext)]);
  await refreshAll();
  bindFilters();
  // Resume-Watcher: wenn beim Tab-Mount noch Bulk-Seed/Assign-Jobs laufen (User hat die Maske
  // verlassen und ist zurück), zeig ein Banner mit Live-Progress und refresh das Grid am Ende.
  void watchResumableBulkJobs(host, ctx, () => void reloadRows());

  // -------------------------------------------------------------------------
  // Detail-Drawer (460px) — macht u. a. die Bestands-Automatik · Min/Max
  // transparent: zeigt kl/Vario/Min/Max, welche Auslöser greifen, den Zielwert
  // und ob ein Push ansteht. Öffnet per Klick auf eine nicht-editierbare Zelle.
  // -------------------------------------------------------------------------
  type StockCalc =
    | { state: 'off' }
    | { state: 'pending'; min: number; max: number; kl: number }
    | { state: 'stockless'; kl: number; min: number; max: number; cMax: boolean; cMin: boolean; target: number; push: boolean }
    | { state: 'on'; kl: number; varioAvail: number; min: number; max: number; cMax: boolean; cMin: boolean; cVar: boolean; target: number; push: boolean };

  function offersStockCalc(row: UnitRow): StockCalc {
    const min = row.min_bestand;
    const max = row.max_bestand;
    if (min == null || max == null) return { state: 'off' };
    const kl = row.stock ?? 0;
    if (isStocklessRow(row)) {
      // Bestandslos: Ziel = Max, kein Vario-Deckel; Auslöser nur kl>Max / kl≤Min.
      const cMax = kl > max;
      const cMin = kl <= min;
      const target = Math.max(0, max);
      return { state: 'stockless', kl, min, max, cMax, cMin, target, push: (cMax || cMin) && target !== kl };
    }
    if (row.vario_stock == null) return { state: 'pending', min, max, kl };
    const varioAvail = Math.trunc(row.vario_stock);
    const cMax = kl > max;
    const cMin = kl <= min;
    const cVar = varioAvail < min;
    const target = Math.max(0, Math.min(varioAvail, max));
    return { state: 'on', kl, varioAvail, min, max, cMax, cMin, cVar, target, push: (cMax || cMin || cVar) && target !== kl };
  }

  function stockSectionHtml(row: UnitRow): string {
    const s = offersStockCalc(row);
    const LAB = '<div class="lab">Bestands-Automatik · Min / Max</div>';
    if (s.state === 'off') {
      return `<div class="dsec">${LAB}<div class="calc-off"><b>Automatik aus.</b><span>Min &amp; Max sind nicht beide gesetzt — für diese Zeile findet keine automatische Bestands-Anpassung statt. Beide Schwellen setzen, um sie zu aktivieren.</span></div></div>`;
    }
    if (s.state === 'pending') {
      return `<div class="dsec">${LAB}<div class="calc-off"><b>Wartet auf Vario-Bestand.</b><span>Min ${s.min} / Max ${s.max} sind gesetzt, aber der Vario-Bestand wurde noch nicht geladen. Die Automatik greift ab dem nächsten Sync.</span></div></div>`;
    }
    const cond = (on: boolean, txt: string, calc: string) =>
      `<div class="cc ${on ? 'on' : ''}"><span class="dot"></span>${txt}<i>${calc}</i></div>`;
    const condsHtml =
      s.state === 'stockless'
        ? cond(s.cMax, 'kl &gt; Max', `${s.kl} &gt; ${s.max}`) + cond(s.cMin, 'kl ≤ Min', `${s.kl} ≤ ${s.min}`)
        : cond(s.cMax, 'kl &gt; Max', `${s.kl} &gt; ${s.max}`) +
          cond(s.cMin, 'kl ≤ Min', `${s.kl} ≤ ${s.min}`) +
          cond(s.cVar, 'Vario &lt; Min', `${s.varioAvail} &lt; ${s.min}`);
    const valsHtml =
      s.state === 'stockless'
        ? `<div class="cv"><span>Kaufland (kl)</span><b>${s.kl}</b></div><div class="cv"><span>Vario</span><b style="font-size:13px">kein Bestand</b></div><div class="cv"><span>Min · Auslöser</span><b>${s.min}</b></div><div class="cv"><span>Max · Ziel</span><b>${s.max}</b></div>`
        : `<div class="cv"><span>Kaufland (kl)</span><b>${s.kl}</b></div><div class="cv"><span>Vario verfügbar</span><b>${s.varioAvail}</b></div><div class="cv"><span>Min · Auslöser</span><b>${s.min}</b></div><div class="cv"><span>Max · Ziel</span><b>${s.max}</b></div>`;
    const targetLabel = s.state === 'stockless' ? 'Zielwert = Max (bestandslos, kein Vario-Deckel)' : 'Zielwert = min(Vario, Max), ≥ 0';
    const verdict = s.push
      ? `<div class="calc-verdict push"><span class="vd"></span>Push steht an — Kaufland-Bestand wird auf <b style="margin:0 3px">${s.target}</b> gesetzt</div>`
      : `<div class="calc-verdict nopush"><span class="vd"></span>Kein Push nötig — Bestand bleibt ${s.kl}</div>`;
    const note =
      s.state === 'stockless'
        ? 'Bestandsloser Artikel: <b>kein Vario-Bestand</b> → Ziel ist <b>Max</b>, ohne Vario-Deckel. Auslöser: Kaufland über Max oder auf/unter Min.'
        : 'Min = <b>Auslöseschwelle</b>, Max = <b>Zielwert</b>, Vario-Verfügbar = <b>Deckel</b>.';
    return `<div class="dsec">${LAB}<div class="calc"><div class="calc-vals">${valsHtml}</div><div class="calc-cond">${condsHtml}</div><div class="calc-target"><span>${targetLabel}</span><b>${s.target}</b></div>${verdict}</div><div class="calc-note">${note} Push läuft im Worker (alle 5 Min / manueller Sync). Der Schalter „Keine Lagerbestand-Änderungen senden" unterdrückt den Push — diese Markierung ignoriert ihn bewusst.</div></div>`;
  }

  function statusPillHtml(row: UnitRow): string {
    if (row.deleted_at) return `<span class="st st-inaktiv"><span class="pd"></span>Gelöscht</span>`;
    const status = row.status ?? 'unknown';
    const cls = status === 'active' ? 'st-aktiv' : status === 'paused' ? 'st-paus' : 'st-inaktiv';
    return `<span class="st ${cls}"><span class="pd"></span>${STATUS_LABEL[status] ?? status}</span>`;
  }

  function vatLabel(row: UnitRow): string {
    const map = gridContext.vatIndicators.get(row.storefront);
    const rate = row.vat_indicator ? map?.get(row.vat_indicator) : undefined;
    if (rate !== undefined && rate !== null) {
      return `${Number.isInteger(rate) ? String(rate) : rate.toFixed(2).replace(/\.?0+$/, '')} %`;
    }
    return row.vat_indicator ? escapeHtml(row.vat_indicator) : '—';
  }

  function buildOffersDrawerHtml(row: UnitRow): string {
    const cur = (cents: number | null) =>
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((cents ?? 0) / 100);
    const groups = gridContext.shippingGroups.get(row.storefront) ?? [];
    const vsgOptions = groups.length
      ? groups
          .map((g) => `<option value="${escapeHtml(g.id_shipping_group)}" ${g.id_shipping_group === row.id_shipping_group ? 'selected' : ''}>${escapeHtml(g.name ?? g.id_shipping_group)}</option>`)
          .join('')
      : `<option>— keine Gruppen geladen —</option>`;
    const varioCell = row.vario_article_id != null
      ? (isStocklessRow(row) ? 'kein Bestand' : row.vario_stock != null ? String(row.vario_stock) : '…')
      : '—';
    const shopUrl = kauflandFrontendUrl(row.storefront, row.id_product);
    const sellerUrl = kauflandBackendUrl(row.storefront, row.id_unit, row.id_offer);
    const varioUrl = row.vario_article_id ? varioArticleUrl(row.vario_article_id) : null;
    const footBtn = (url: string | null, label: string) =>
      url
        ? `<a class="btn btn-ghost" href="${escapeHtml(url)}" target="_blank" rel="noopener">${KF_ICON.external}${label}</a>`
        : `<button type="button" class="btn btn-ghost" disabled>${KF_ICON.external}${label}</button>`;
    return `
      <div class="dh">
        <div><div class="dt">Angebot</div><div class="doid">${escapeHtml(row.id_offer ?? row.id_unit)}</div></div>
        <span style="margin-left:4px">${statusPillHtml(row)}</span>
        <button type="button" class="dclose" data-kf-drawer-close aria-label="Schließen">${KF_ICON.close}</button>
      </div>
      <div class="db">
        <div class="dtitle">${escapeHtml(row.title ?? row.ean ?? '—')}</div>
        <div class="dsec"><div class="lab">Identifikation</div><div class="dcols">
          <div class="kv2"><div class="k">Vario-SKU</div><div class="v mono ${row.vario_article_number ? '' : 'amber'}">${row.vario_article_number ? escapeHtml(row.vario_article_number) : 'nicht zugeordnet'}</div></div>
          <div class="kv2"><div class="k">EAN</div><div class="v mono">${escapeHtml(row.ean ?? '—')}</div></div>
        </div></div>
        <div class="dsec"><div class="lab">Preise</div><div class="dcols">
          <div class="kv2"><div class="k">Preis</div><div class="v">${cur(row.price_cents)}</div></div>
          <div class="kv2"><div class="k">Min-Preis</div><div class="v">${row.minimum_price_cents != null ? cur(row.minimum_price_cents) : '—'}</div></div>
          <div class="kv2"><div class="k">MwSt</div><div class="v">${vatLabel(row)}</div></div>
        </div></div>
        <div class="dsec"><div class="lab">Bestand</div><div class="dcols">
          <div class="kv2"><div class="k">Kaufland</div><div class="v">${row.stock ?? 0}</div></div>
          <div class="kv2"><div class="k">Vario</div><div class="v">${escapeHtml(varioCell)}</div></div>
        </div></div>
        ${stockSectionHtml(row)}
        <div class="dsec"><div class="lab">Versand &amp; Sync</div><div class="dcols">
          <div class="kv2"><div class="k">Versandgruppe</div><div class="v"><select class="d-select" data-kf-vsg ${groups.length ? '' : 'disabled'}>${vsgOptions}</select></div></div>
          <div class="kv2"><div class="k">Aktualisiert</div><div class="v">${escapeHtml(formatRelative(row.fetched_at))}</div></div>
        </div></div>
      </div>
      <div class="dfoot off-dfoot">
        ${footBtn(shopUrl, 'Kaufland-Shop')}
        ${footBtn(sellerUrl, 'Sellerportal')}
        ${footBtn(varioUrl, 'Vario-Artikel')}
        <button type="button" class="btn btn-primary" data-kf-map>${KF_ICON.link}Zuordnung ändern</button>
      </div>`;
  }

  function openOffersDrawer(row: UnitRow): void {
    // Kein `wide`: Die Breite kommt seit dem Drawer-Umbau aus dem Basis-Stil
    // (`.drawer{width:50vw;max-width:min(900px,92vw)}`). `.drawer.wide` traegt nur noch
    // Regeln fuer `.dhead`/`.dbody`/`.dfoot`, die dieser Drawer nicht nutzt — er hat
    // `.dh`/`.db`. Das Flag waere hier also wirkungslos und nur irrefuehrend.
    drawer.open({
      html: buildOffersDrawerHtml(row),
      onMount: (content) => {
        content.querySelector<HTMLSelectElement>('[data-kf-vsg]')?.addEventListener('change', (e) => {
          const sel = e.target as HTMLSelectElement;
          const newId = sel.value;
          if (!newId || newId === row.id_shipping_group) return;
          row.id_shipping_group = newId;
          void applyShippingGroupChange(ctx, row.storefront, newId, [row.id_unit]);
          showToast(`Versandgruppe → ${sel.options[sel.selectedIndex]?.text ?? newId}`, 'success');
          gridApi?.refreshCells({ force: true });
        });
        content.querySelector<HTMLElement>('[data-kf-map]')?.addEventListener('click', () => {
          if (!row.id_unit) return;
          openArticleDialog({
            supabase: ctx.supabase,
            supabaseUrl: ctx.supabaseUrl,
            session: ctx.session,
            unit: {
              storefront: row.storefront,
              idUnit: row.id_unit,
              ean: row.ean,
              sku: row.id_offer,
              title: row.title,
              varioArticleId: row.vario_article_id,
              varioArticleNumber: row.vario_article_number,
            },
            onDone: () => {
              drawer.close();
              reloadRows();
            },
          });
        });
      },
    });
  }

  function bindFilters() {
    const search = host.querySelector<HTMLInputElement>('#kl-offers-search')!;
    const clearBtn = host.querySelector<HTMLButtonElement>('#kl-offers-search-clear')!;
    const updateClearVisibility = () => {
      clearBtn.classList.toggle('show', search.value.length > 0);
    };
    updateClearVisibility();
    let debounce: number | null = null;
    search.addEventListener('input', () => {
      updateClearVisibility();
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        filters.search = search.value;
        void reloadRows();
      }, 300) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      search.value = '';
      updateClearVisibility();
      filters.search = '';
      void reloadRows();
      search.focus();
    });
    host.querySelector<HTMLSelectElement>('#kl-offers-status')!.addEventListener('change', (e) => {
      filters.status = (e.target as HTMLSelectElement).value || null;
      void reloadRows();
    });
    host.querySelector<HTMLSelectElement>('#kl-offers-storefront')!.addEventListener('change', (e) => {
      filters.storefront = (e.target as HTMLSelectElement).value || null;
      void reloadRows();
    });
    host.querySelector<HTMLSelectElement>('#kl-offers-mapping')!.addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value;
      filters.mapping = v === 'mapped' || v === 'unmapped' || v === 'exists-unmapped' ? v : null;
      // Mapping-Filter wechseln → die Toolbar zeigt evtl. andere Buttons (Seed-Bulk-Button
      // verschwindet im „existiert in Vario, nicht zugeordnet"-Filter, weil die Treffer
      // dort definitionsgemäß schon einen Vario-Artikel haben).
      renderToolbar(host, pending, ctx, gridApi, filters, () => void refreshAll());
      void reloadRows();
    });
    bindLegendPopover();
  }

  function bindLegendPopover() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-offers-legend-btn');
    const pop = host.querySelector<HTMLDivElement>('#kl-offers-legend-popover');
    if (!btn || !pop) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !pop.hidden;
      pop.hidden = isOpen;
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
    // Klick außerhalb → schließen. Auf document, weil das Popover selbst evtl. innere Klicks
    // verarbeitet (Links etc.).
    document.addEventListener('click', (e) => {
      if (pop.hidden) return;
      const target = e.target as Node;
      if (!pop.contains(target) && !btn.contains(target)) {
        pop.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  async function refreshAll() {
    const summary = await loadSummary(ctx);
    const activated = await loadActivatedStorefronts(ctx.supabase);
    const isFirstInit = !gridApi;
    // Nur beim ersten Aufbau vorbelegen. refreshAll laeuft auch nach jeder Aktion, und dort
    // wuerde eine erneute Vorbelegung die Auswahl des Nutzers zurueckwerfen.
    if (isFirstInit) {
      filters.storefront = resolveInitialStorefront(activated, currentVarioSalesChannelId());
    }
    populateStorefrontFilter(host, summary, activated, filters);
    if (isFirstInit) {
      gridApi = initGrid(
        host,
        ctx,
        pending,
        filters,
        () => renderToolbar(host, pending, ctx, gridApi, filters, () => void refreshAll()),
        gridContext,
        (row) => openOffersDrawer(row),
      );
      bindSyncButton();
      // First init: AG Grid fetches automatically from the datasource — no manual purge needed.
    } else {
      reloadRows();
    }
    renderToolbar(host, pending, ctx, gridApi, filters, () => void refreshAll());
  }

  function reloadRows() {
    if (!gridApi) return;
    // Run on the next tick — AG Grid prohibits cache invalidation while it is still rendering the
    // initial set, and we may be called from inside the same frame as createGrid().
    setTimeout(() => gridApi?.purgeInfiniteCache(), 0);
  }

  function bindSyncButton() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-offers-refresh');
    const icon = host.querySelector<HTMLElement>('#kl-offers-refresh-icon');
    if (!btn || !icon) return;
    btn.addEventListener('click', async () => {
      const sf = filters.storefront;
      // Pick all activated storefronts the tenant has when no specific filter is set.
      const targets = sf ? [sf] : await listActiveStorefronts(ctx);
      if (targets.length === 0) return;
      btn.disabled = true;
      icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
      // User-Hinweis: bei grossen Tenants (>1000 Mappings) braucht der Vario-Stock-
      // Refresh mehrere Minuten, weil pro Article ein Vario-API-Call laeuft.
      // 5 min ist die obere Schaetzung — kleinere Tenants sind in <30 s durch.
      showToast('Sync läuft, kann bis zu 5 Minuten dauern.', 'info');
      try {
        // Komplettabgleich: Kaufland-Units-Refresh + Vario-Stock-Refresh + Stock-Push
        // (storefront-spezifisch suppress-aware). Enqueued mehrere Jobs gestaffelt via
        // run_after — der Polling-Loop wartet bis alle durch sind.
        for (const s of targets) {
          await ctx.supabase.rpc('kaufland_enqueue_storefront_full_sync', { _storefront: s });
        }
        await pollUntilSyncIdle(ctx);
        reloadRows();
        showToast('Sync abgeschlossen.', 'success');
      } catch (e) {
        // Voller Error-Dump in der Konsole, damit Browser-Stack einen `null`-DOM-Crash
        // o.ä. eindeutig zuordnen lässt. User-Toast bleibt kurz.
        console.error('[offers] manual sync failed', e instanceof Error ? e.stack : e);
        const msg = e instanceof Error ? e.message : String(e);
        showToast(`Sync fehlgeschlagen: ${msg}`, 'error');
      } finally {
        btn.disabled = false;
        icon.className = 'v-icon fa-regular fa-rotate-right';
      }
    });
  }

  async function pollUntilSyncIdle(ctx: OffersCtx): Promise<void> {
    // Loop until kaufland.units_sync_runs has no row with status='running' for this tenant.
    // Deadline: 5 min — Vario-Stock-Refresh fuer grosse Tenants (>1000 Mappings)
    // braucht laenger als 60 s, vorheriger Cap hat den Poll vorzeitig abgebrochen.
    const deadline = Date.now() + 5 * 60_000;
    while (Date.now() < deadline) {
      const { data } = await ctx.supabase
        .schema('kaufland')
        .from('units_sync_runs')
        .select('id')
        .eq('status', 'running')
        .limit(1);
      if (!data || data.length === 0) return;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  }
}

/**
 * Beim Tab-Mount prüfen, ob noch Bulk-Assign-Jobs für diesen Tenant laufen — z. B. weil der
 * User die Maske verlassen hat, während der Worker mitten in einem 200er-Lauf war. Wenn ja,
 * Banner einblenden, alle Jobs mit Live-Progress beobachten und am Ende das Grid refreshen.
 *
 * Idempotent: ein einmal laufender Watcher signalisiert sich via `dataset.active`, mehrfache
 * Aufrufe (z. B. nach Filter-Wechsel) bleiben no-op.
 */
async function watchResumableBulkJobs(
  host: HTMLElement,
  ctx: OffersCtx,
  onAllDone: () => void,
): Promise<void> {
  const banner = host.querySelector<HTMLDivElement>('#kl-offers-resume-banner');
  if (!banner) return;
  if (banner.dataset.active === '1') return;

  const { data, error } = await ctx.supabase
    .schema('account')
    .from('jobs')
    .select('id,type,progress,created_at')
    .in('type', ['kaufland.bulk_assign_vario_articles'])
    .in('status', ['queued', 'running'])
    .order('created_at', { ascending: true });
  if (error || !data || data.length === 0) return;

  banner.dataset.active = '1';
  const perJob = new Map<string, JobProgress>();
  const jobs = data as Array<{ id: string; type: string; progress: JobProgress | null }>;
  for (const j of jobs) {
    if (j.progress) perJob.set(j.id, j.progress);
  }
  const verb = 'zugeordnet';

  const renderBanner = () => {
    let proc = 0;
    let total = 0;
    for (const p of perJob.values()) {
      proc += p.processed ?? 0;
      total += p.total ?? 10;
    }
    // Sicherheits-Cap: wenn perJob noch nicht für alle Jobs befüllt ist (Worker hat noch
    // nichts geschrieben), nutzen wir 10 (= aktuelle Chunk-Size) als Schätzung pro Job.
    const effectiveTotal = Math.max(total, jobs.length * 10);
    banner.innerHTML = `
      <span class="v-icon fa-regular fa-spinner-third fa-spin"></span>
      <div><strong>${proc} von ${effectiveTotal}</strong> ${verb} — der Vorgang läuft im Hintergrund weiter, du kannst die Maske verlassen.</div>
    `;
  };
  banner.className = 'kf-banner';
  banner.hidden = false;
  renderBanner();

  await Promise.all(
    jobs.map(async (j) => {
      try {
        await awaitJob(ctx.supabase, j.id, {
          timeoutMs: 30 * 60 * 1000,
          pollIntervalMs: 2000,
          onProgress: (row) => {
            if (row.progress) {
              perJob.set(j.id, row.progress);
              renderBanner();
            }
          },
        });
      } catch (e) {
        console.warn('[offers] resume watcher failed for job', j.id, e);
      }
    }),
  );

  banner.hidden = true;
  banner.className = '';
  banner.innerHTML = '';
  delete banner.dataset.active;
  onAllDone();
}

async function listActiveStorefronts(ctx: OffersCtx): Promise<string[]> {
  return (await loadActivatedStorefronts(ctx.supabase)).map((r) => r.storefront);
}

// ---------------------------------------------------------------------------
// AG Grid setup
// ---------------------------------------------------------------------------

function initGrid(
  host: HTMLElement,
  ctx: OffersCtx,
  pending: Map<string, PendingEdit>,
  filters: FilterState,
  renderToolbarFn: () => void,
  gridContext: OffersGridContext,
  onRowOpen: (row: UnitRow) => void,
): GridApi<UnitRow> {
  const eGrid = host.querySelector<HTMLDivElement>('#kl-grid')!;

  // Helper: cellClassRule für "Cell hat unsavedte Änderung". Wir lesen aus der pending-Map
  // (closure), AG-Grid evaluiert die Funktion bei jedem Row-Render + nach refreshCells.
  const pendingRule = (jobField: keyof PendingEdit['fields']) => (p: { data: UnitRow | undefined }) => {
    const row = p.data;
    if (!row) return false;
    return pending.get(`${row.storefront}::${row.id_unit}`)?.fields[jobField] !== undefined;
  };

  /**
   * Preisfelder gehen seit 2026-08-02 OHNE Speichern-Klick raus (wie Min/Max). Solange die
   * Übertragung läuft, bleibt die Zelle markiert — dieses Set trägt die laufenden Felder.
   * Bewusst getrennt von `pending`: sonst erschiene die Speichern-Leiste für etwas, das
   * bereits unterwegs ist.
   */
  const inflight = new Set<string>();
  const inflightKey = (row: UnitRow, field: string) => `${row.storefront}::${row.id_unit}::${field}`;
  const inflightRule = (jobField: string) => (p: { data: UnitRow | undefined }) =>
    p.data ? inflight.has(inflightKey(p.data, jobField)) : false;

  const cols: ColDef<UnitRow>[] = [
    // Die Checkbox-Spalte wird via `selectionColumnDef` (siehe GridOptions unten) als eigene
    // pinned-left-Spalte gerendert — sie steht VOR der SF-Spalte und enthält die Header-
    // Checkbox zum Markieren aller (geladenen) Zeilen.
    {
      field: 'storefront',
      headerName: 'SF',
      width: 60,
      pinned: 'left',
      cellRenderer: storefrontRenderer,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'id_offer',
      headerName: 'SKU',
      // Bemessen auf 20 Zeichen: 20 × 6,9px (Monospace ≈ 0,6 × 11,5px) = 138px, dazu das
      // reduzierte Padding von 2 × 12px. Laengere SKUs werden per `kl-ell-cell`
      // abgeschnitten, der volle Wert steht im Tooltip. Die 11,5px kommen aus
      // `--kl-mono-cell-fs`; frueher 9px, was die Spalte kleiner wirken liess als den Titel.
      width: 164,
      minWidth: 164,
      // Default-Sortierung beim Tab-Öffnen. Der User kann jederzeit auf einen
      // anderen Header klicken; nach Tab-Wechsel oder Reload greift wieder SKU desc.
      sort: 'desc',
      cellStyle: MONO_CELL,
      headerClass: 'kl-th-mono',
      cellClass: 'kl-ell-cell',
      tooltipField: 'id_offer',
    },
    {
      field: 'vario_article_number',
      headerName: 'Vario-SKU',
      headerTooltip: 'Artikelnummer des zugeordneten Vario-Artikels. Leer = noch kein Mapping.',
      // Gleiche Bemessung wie die SKU-Spalte, siehe dort.
      width: 164,
      minWidth: 164,
      cellStyle: MONO_CELL,
      headerClass: 'kl-th-mono',
      cellClass: 'kl-ell-cell',
      tooltipField: 'vario_article_number',
    },
    // Keine EAN-Spalte: sie kostet Breite und steht ohnehin im Detail-Pane. Die EAN bleibt
    // trotzdem suchbar — die Suche laeuft serverseitig in applyOffersFilters() ueber
    // `ean.ilike`, unabhaengig von den Grid-Spalten. Das Feld bleibt deshalb auch im
    // select() von loadUnitsPage stehen.
    { field: 'title', headerName: 'Titel', flex: 2, minWidth: 250, cellClass: 'kl-ell-cell', tooltipField: 'title' },
    {
      // Bewusst NICHT editierbar (2026-08-02). Aktivieren und Pausieren laeuft ueber die
      // Zeilenauswahl und die Bulk-Leiste — ein Weg statt zwei. Der frueher hier haengende
      // `agSelectCellEditor` bot zudem die Rohwerte `active`/`paused` unuebersetzt an und
      // liess beim Aufklappen die Liste leer erscheinen.
      field: 'status',
      headerName: 'Status',
      width: 80,
      cellRenderer: statusRenderer,
    },
    {
      field: 'price_cents',
      headerName: 'Preis',
      width: 110,
      ...NUM_HEADER,
      editable: true,
      cellClassRules: {
        'kl-cell-pending': inflightRule('listingPriceCents'),
        'kl-cell-editable': (p) => !inflightRule('listingPriceCents')(p),
      },
      cellEditor: 'agNumberCellEditor',
      // UI works in EUR; convert at the edge.
      valueGetter: (p) => centsToEur(p.data?.price_cents ?? null),
      valueFormatter: eurFormatter,
      valueSetter: makeCentsSetter(ctx, inflight, inflightKey, 'listingPriceCents', renderToolbarFn, 'price_cents'),
      cellStyle: NUM_CELL,
    },
    {
      field: 'minimum_price_cents',
      headerName: 'Min-Preis',
      width: 110,
      ...NUM_HEADER,
      editable: true,
      cellClassRules: {
        'kl-cell-pending': inflightRule('minimumPriceCents'),
        'kl-cell-editable': (p) => !inflightRule('minimumPriceCents')(p),
      },
      cellEditor: 'agNumberCellEditor',
      valueGetter: (p) => centsToEur(p.data?.minimum_price_cents ?? null),
      valueFormatter: eurFormatter,
      valueSetter: makeCentsSetter(ctx, inflight, inflightKey, 'minimumPriceCents', renderToolbarFn, 'minimum_price_cents'),
      cellStyle: NUM_CELL,
    },
    {
      field: 'stock',
      // Header gekuerzt: der ausgeschriebene Titel braucht bei 11px Versalien mehr Platz als
      // die sieben Ziffern darunter und wuerde die Spaltenbreite allein bestimmen.
      headerName: 'Verf.',
      headerTooltip: 'Verfügbarer Bestand bei Kaufland',
      ...NUM_COL,
      editable: true,
      // Reihenfolge der Klassen ist wichtig für CSS-Spezifität: pending (oranger Push pending)
      // > stock-will-change (oranger Hinweis durch Min/Max) > editable (helles Blau).
      cellClassRules: {
        'kl-cell-pending': pendingRule('amount'),
        'kl-cell-stock-will-change': (p) => !pendingRule('amount')(p) && willStockChange(p.data),
        'kl-cell-editable': (p) => !pendingRule('amount')(p) && !willStockChange(p.data),
      },
      cellEditor: 'agNumberCellEditor',
      valueSetter: makeFieldSetter(
        pending,
        'amount',
        (raw) => (typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)),
        renderToolbarFn,
        'stock',
      ),
      cellStyle: NUM_CELL,
    },
    {
      field: 'vario_stock',
      headerName: 'Vario',
      headerTooltip: 'Aktuell verfügbarer Bestand aus Vario (stockAvailable). Wird nach jeder Mapping-Zuordnung und nach jedem Listings-Sync automatisch aktualisiert. „o. B." = Artikel ohne Bestandsführung (Dienstleistung oder WITHOUT_STOCK) — dort zählt nur Min/Max.',
      ...NUM_COL,
      editable: false,
      valueFormatter: varioStockFormatter,
      cellStyle: NUM_CELL,
    },
    {
      field: 'min_bestand',
      headerName: 'Min',
      headerTooltip:
        'Min-Bestand (optional). Trigger: sobald Kaufland-Bestand ≤ Min ODER Vario-Verfügbar < Min, wird auf Max aufgefüllt (gekappt durch Vario). Nur aktiv wenn beide Felder gesetzt sind. Leer lassen = Feature aus.',
      ...NUM_COL,
      editable: (p) => p.data?.vario_article_id != null,
      cellClassRules: { 'kl-cell-editable': (p) => p.data?.vario_article_id != null },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 0 },
      valueSetter: makeMinMaxSetter(ctx, 'min', renderToolbarFn),
      cellStyle: NUM_CELL,
    },
    {
      field: 'max_bestand',
      headerName: 'Max',
      headerTooltip:
        'Max-Bestand (optional). Zielwert beim Auffüllen. Nur aktiv wenn auch Min gesetzt ist. Leer lassen = Feature aus.',
      ...NUM_COL,
      editable: (p) => p.data?.vario_article_id != null,
      cellClassRules: { 'kl-cell-editable': (p) => p.data?.vario_article_id != null },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 0 },
      valueSetter: makeMinMaxSetter(ctx, 'max', renderToolbarFn),
      cellStyle: NUM_CELL,
    },
    // Aktualisiert / Versandgruppe / MwSt sind aus dem Grid entfernt — sie leben nur noch
    // im Detail-Drawer (Aktualisiert + Versandgruppe-Select unter „Versand & Sync", MwSt
    // unter „Preise"). Das gibt der Titel-Spalte (einzige Flex-Spalte) mehr Raum; Min/Max
    // bleiben bewusst im Grid sichtbar.
    {
      headerName: 'Aktionen',
      headerTooltip: 'Kaufland-Shop / Seller-Portal / Vario-Artikel zuweisen',
      width: 150,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (p: ICellRendererParams<UnitRow>) => actionsRenderer(p, ctx),
      cellStyle: { textAlign: 'center' },
    },
  ];

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { rows, total } = await loadUnitsPage(ctx, filters, params);
        let lastRow: number;
        if (total !== null) {
          lastRow = total;
        } else if (rows.length < params.endRow - params.startRow) {
          lastRow = params.startRow + rows.length;
        } else {
          lastRow = -1;
        }
        params.successCallback(rows, lastRow);
      } catch (e) {
        console.error('[offers] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  const options: GridOptions<UnitRow> = {
    columnDefs: cols,
    context: gridContext,
    defaultColDef: { sortable: true, filter: false, resizable: true },
    // AG Grid v32.2+ object form. Click-selection off so cell-edits don't accidentally toggle the row.
    // `headerCheckbox: true` rendert eine Master-Checkbox im Spaltenkopf, die alle (geladenen)
    // Zeilen toggelt — beim Infinite-Row-Model nur die im Cache, was hier dem typischen
    // „Alles auf der aktuellen Page" entspricht.
    rowSelection: {
      mode: 'multiRow',
      checkboxes: true,
      // KEIN `headerCheckbox: true` — beim Infinite Row Model ungültig (AG-Grid-Warnung
      // „headerCheckbox is only available if using clientSide/serverSide"). Die „Alle
      // markieren"-Kopf-Checkbox liefert die Custom-`SelectAllHeader` (headerComponent).
      enableClickSelection: false,
    },
    // Dedizierte Checkbox-Spalte als erste pinned-left-Spalte. Ohne `selectionColumnDef`
    // würde AG-Grid die Checkboxen in die SF-Spalte mergen — dort überschreibt aber
    // `storefrontRenderer` das Zell-DOM komplett mit der Länderflagge, sodass die
    // Checkboxen unsichtbar wären.
    //
    // Header-Checkbox: AG-Grid v32 deaktiviert `rowSelection.headerCheckbox` beim Infinite
    // Row Model komplett (siehe `selectAllFeature.checkRightRowModelType` — erlaubt nur
    // clientSide/serverSide). Daher rendern wir die „Alle markieren"-Checkbox via
    // Custom-HeaderComponent, die alle aktuell im Cache geladenen Rows selektiert.
    selectionColumnDef: {
      pinned: 'left',
      width: 44,
      maxWidth: 44,
      minWidth: 44,
      sortable: false,
      resizable: false,
      headerTooltip: 'Alle Zeilen der aktuellen Page markieren.',
      headerComponent: SelectAllHeader,
      // Header- und Body-Cell teilen `.kl-selection-cell` — CSS setzt padding:0 + flex-center,
      // damit Header-Checkbox exakt über den Spalten-Checkboxen sitzt. Standard-AG-Grid
      // gibt dem Header sonst --ag-cell-horizontal-padding (16px), Body-Cells aber nicht.
      headerClass: 'kl-selection-cell',
      cellClass: 'kl-selection-cell',
      cellStyle: {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    stopEditingWhenCellsLoseFocus: true,
    enableCellTextSelection: true,
    rowModelType: 'infinite',
    cacheBlockSize: GRID_BLOCK_SIZE,
    maxBlocksInCache: GRID_CACHE_BLOCKS,
    infiniteInitialRowCount: 50,
    blockLoadDebounceMillis: 100,
    datasource,
    // Counter „X markiert" in der Toolbar muss sich live mitbewegen, sobald der User
    // einzelne Row-Checkboxen togglet — sonst zeigt der Button noch die alte Anzahl
    // nach dem Ab-/Anwählen.
    onSelectionChanged: renderToolbarFn,
    // Selection ist strikt page-lokal: bei Page-Wechsel oder Page-Size-Change die
    // vorherige Selektion verwerfen. Sonst leben alte Selektionen im AG-Grid-Cache
    // weiter und Bulk-Aktionen wirken überraschend auf Rows, die der User nicht
    // mehr sieht (87 statt 50 markiert).
    onPaginationChanged: (params) => {
      if (params.newPage || params.newPageSize) params.api.deselectAll();
    },
    // Klick auf eine nicht-editierbare, nicht-Aktions-Zelle öffnet den Detail-Drawer
    // (u. a. mit der Bestands-Automatik · Min/Max). Editierbare Zellen → Inline-Edit;
    // Aktions-Spalte + Auswahl-Checkbox + interaktive Elemente behalten ihre eigenen Handler.
    onCellClicked: (e) => {
      const colId = e.column?.getColId?.() ?? '';
      // AG-Grid-interne Spalten (Auswahl-Checkbox = `ag-Grid-ControlsColumn` in v32.3,
      // früher `ag-Grid-SelectionColumn`) NICHT als Drawer-Öffner behandeln — sonst öffnet
      // ein Checkbox-Klick den Detail-Drawer, dessen Scrim die Bulk-Action-Bar verdeckt.
      // `ag-Grid-`-Präfix deckt alle internen Spalten versionsfest ab.
      if (colId === 'actions' || colId.startsWith('ag-Grid-')) return;
      if (e.colDef?.editable) return;
      const target = e.event?.target as HTMLElement | null;
      if (target?.closest('button, a, input, select, label, [data-act], .ag-checkbox-input-wrapper, .kl-selection-cell')) return;
      if (e.data) onRowOpen(e.data);
    },
    // Classic pagination footer on top of the infinite server-fetch — every page change just
    // jumps the viewport, the data still streams lazily in 100-row blocks.
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 200],
    rowClassRules: {
      'kl-row-deleted': (p) => p.data?.deleted_at != null,
      // Hellblau: noch kein Mapping, aber Vario-Artikel existiert (via EAN/SKU gematched) →
      // ein Klick auf das Zuweisen-Icon übernimmt den Vorschlag ins Mapping.
      'kl-row-vario-suggested': (p) =>
        p.data != null &&
        p.data.vario_article_id == null &&
        p.data.suggested_vario_article_id != null &&
        p.data.deleted_at == null,
      // Hellrot: noch keinem Vario-Artikel zugeordnet UND kein Auto-Vorschlag — User muss
      // entweder einen Artikel manuell zuweisen oder anlegen. Gelöschte Zeilen bekommen den
      // Hinweis nicht (kl-row-deleted dominiert), zugewiesene Zeilen bleiben farblos.
      'kl-row-vario-unmapped': (p) =>
        p.data != null &&
        p.data.vario_article_id == null &&
        p.data.suggested_vario_article_id == null &&
        p.data.deleted_at == null,
    },
    // Infinite Row Model emits loading-rows without `data` — guard against that so AG Grid
    // doesn't throw and silently swallow the rest of the page.
    getRowId: (p) => (p.data ? `${p.data.storefront}::${p.data.id_unit}` : ''),
  };
  return createGrid(eGrid, options);
}

/**
 * Echos die Min/Max-Trigger-Logik aus `apps/worker/src/jobs/kaufland-stock-sync.ts` —
 * gibt true zurück, wenn der nächste Stock-Sync den Kaufland-Bestand verändern würde.
 * Wird vom Frontend für die visuelle Markierung der Bestand-Zelle genutzt
 * („Achtung: ändert sich beim nächsten Sync"). Suppress-Stock-Push ist Storefront-weit
 * und wird hier ignoriert; bei aktiviertem Suppress läuft der Push einfach nicht, die
 * Markierung wäre dann harmlos „lügt aber stimmt trotzdem".
 */
function willStockChange(row: UnitRow | undefined): boolean {
  if (!row) return false;
  if (row.min_bestand == null || row.max_bestand == null) return false;
  if (row.stock == null) return false;
  const kl = row.stock;
  const min = row.min_bestand;
  const max = row.max_bestand;
  if (isStocklessRow(row)) {
    // Bestandslos: Ziel = Max, kein vario_available-Deckel. Trigger: kl>max oder kl<=min.
    if (!(kl > max || kl <= min)) return false;
    return Math.max(0, max) !== kl;
  }
  if (row.vario_stock == null) return false;
  const varioAvail = Math.trunc(row.vario_stock);
  const trigger = kl > max || kl <= min || varioAvail < min;
  if (!trigger) return false;
  const target = Math.max(0, Math.min(varioAvail, max));
  return target !== kl;
}

function makeFieldSetter<TVal>(
  pending: Map<string, PendingEdit>,
  jobField: keyof PendingEdit['fields'],
  parse: (raw: unknown) => TVal,
  onChange: () => void,
  rowField?: keyof UnitRow,
) {
  return (params: ValueSetterParams<UnitRow>): boolean => {
    if (!params.data) return false;
    const parsed = parse(params.newValue);
    if (parsed === undefined || (typeof parsed === 'number' && Number.isNaN(parsed))) return false;
    upsertPending(pending, params.data, jobField, parsed as unknown);
    const target = params.data as unknown as Record<string, unknown>;
    target[(rowField ?? (jobField as string)) as string] = parsed as unknown;
    onChange();
    // pending hat sich geändert → cellClassRules für die Row neu evaluieren.
    if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });
    return true;
  };
}

/**
 * Setter für Preis und Mindestpreis — seit 2026-08-02 OHNE Speichern-Klick, analog Min/Max.
 *
 * Der Wert geht sofort per `kaufland_enqueue_units_update` raus. Die Zelle bleibt markiert,
 * solange der Job läuft, und wird bei Erfolg normal; bei Fehler springt der alte Wert zurück
 * und ein Toast nennt den Grund.
 *
 * Der Job schickt den Preis an Kaufland UND schreibt ihn in den Vario-Verkaufskanal. Letzteres
 * ist kein Beiwerk: Der Cron `kaufland-price-sync-15m` nimmt den Vario-Kanalpreis als Quelle
 * und würde eine nur bei Kaufland gesetzte Änderung beim nächsten Lauf zurückdrehen.
 */
function makeCentsSetter(
  ctx: OffersCtx,
  inflight: Set<string>,
  inflightKey: (row: UnitRow, field: string) => string,
  jobField: 'listingPriceCents' | 'minimumPriceCents',
  onChange: () => void,
  rowField: 'price_cents' | 'minimum_price_cents',
) {
  return (params: ValueSetterParams<UnitRow>): boolean => {
    if (!params.data) return false;
    const row = params.data;
    const eur =
      typeof params.newValue === 'number'
        ? params.newValue
        : Number.parseFloat(String(params.newValue).replace(',', '.'));
    if (Number.isNaN(eur)) return false;
    const cents = Math.round(eur * 100);
    const target = row as unknown as Record<string, unknown>;
    const oldValue = target[rowField] as number | null;
    if (cents === oldValue) return false;

    // Optimistisch anzeigen und als „unterwegs" markieren.
    target[rowField] = cents;
    const key = inflightKey(row, jobField);
    inflight.add(key);
    onChange();
    const node = params.node;
    const refresh = () => {
      if (node) params.api.refreshCells({ rowNodes: [node], force: true });
    };
    refresh();

    // Wird true, wenn Kaufland den Preis noch nicht bestaetigt hat: dann behaelt die Zelle ihre
    // Markierung ueber das Job-Ende hinaus.
    let keepMarked = false;

    void (async () => {
      try {
        const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_units_update', {
          _storefront: row.storefront,
          _updates: [{ idUnit: row.id_unit, [jobField]: cents }],
        });
        if (error) throw new Error(error.message);
        const jobId = Array.isArray(data)
          ? (data[0]?.job_id as string)
          : (data as { job_id?: string } | null)?.job_id;
        if (!jobId) throw new Error('Kein job_id zurückgeliefert');
        const job = await awaitJob(ctx.supabase, jobId, 60_000);
        if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
        // `completed` heisst nur, dass der Job durchgelaufen ist. Ob Kaufland die Aenderung
        // angenommen hat, steht in `result.results[]` — sonst bliebe ein abgelehnter Preis
        // unbeanstandet in der Liste stehen.
        const outcome = readUnitUpdateOutcome(job.result, row.id_unit);
        if (!outcome.ok) throw new Error(outcome.error ?? 'Kaufland hat die Änderung abgelehnt.');
        // Der Worker hat nach dem Push bei Kaufland nachgelesen. Solange Kaufland den neuen
        // Preis nicht zurueckliefert, bleibt die Zelle markiert — ein quittierter PATCH ist
        // kein gefuehrter Preis. Der Zwei-Stunden-Abgleich raeumt spaeter auf.
        if (outcome.priceConfirmed && outcome.priceConfirmed !== 'confirmed') {
          keepMarked = true;
          showToast(
            outcome.priceConfirmed === 'mismatch'
              ? 'Kaufland führt den neuen Preis noch nicht. Die Änderung bleibt markiert, bis der nächste Abgleich sie bestätigt.'
              : 'Der neue Preis ließ sich bei Kaufland nicht gegenprüfen. Die Änderung bleibt markiert, bis der nächste Abgleich sie bestätigt.',
            'info',
          );
        }
        if (jobField === 'listingPriceCents' && outcome.varioPriceFailed) {
          // Kein Revert: bei Kaufland steht der neue Preis. Aber ohne Vario-Gegenstueck
          // dreht ihn `kaufland-price-sync-15m` binnen einer Viertelstunde zurueck.
          showToast(
            'Preis bei Kaufland gesetzt, aber nicht in den Vario-Verkaufskanal geschrieben. ' +
              'Er wird beim nächsten Preisabgleich zurückgesetzt.',
            'error',
          );
        }
      } catch (e) {
        // Zurück auf den alten Wert — sonst zeigt die Liste einen Preis, den weder Kaufland
        // noch Vario kennt.
        target[rowField] = oldValue;
        keepMarked = false;
        showToast(`Preis nicht übernommen: ${(e as Error).message}`, 'error');
      } finally {
        if (!keepMarked) inflight.delete(key);
        onChange();
        refresh();
      }
    })();

    return true;
  };
}

/**
 * Setter für Min/Max-Bestand-Spalten — KEIN pending-Pattern wie bei Preis/Stock, weil die Werte
 * NICHT an Kaufland gepusht werden, sondern nur als Setting in `vario.article_mappings` liegen.
 * Persistierung erfolgt direkt per RPC `kaufland_set_listing_min_max`. Bei Fehler wird der lokale
 * Wert revertet und der Toolbar-Renderer angetriggert (zeigt Save-State auf Stand).
 *
 * Leere Eingabe (NULL): „Feature aus" — sendet null an die RPC. Negative Werte werden bereits
 * client-side abgefangen, die RPC validiert nochmal als Defense-in-Depth.
 */
function makeMinMaxSetter(
  ctx: OffersCtx,
  which: 'min' | 'max',
  onChange: () => void,
) {
  return (params: ValueSetterParams<UnitRow>): boolean => {
    if (!params.data) return false;
    const row = params.data;
    const rowField = which === 'min' ? 'min_bestand' : 'max_bestand';
    const otherField = which === 'min' ? 'max_bestand' : 'min_bestand';

    // Wert parsen: leere Eingabe → null. Zahl → Integer ≥ 0. NaN → abbrechen.
    let parsed: number | null;
    if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
      parsed = null;
    } else {
      const n = typeof params.newValue === 'number' ? params.newValue : Number.parseInt(String(params.newValue), 10);
      if (Number.isNaN(n) || n < 0) return false;
      parsed = Math.trunc(n);
    }

    const oldValue = (row as unknown as Record<string, unknown>)[rowField] as number | null;
    if (parsed === oldValue) return false;

    // Optimistisch lokal updaten, damit AG-Grid die Zelle sofort rendert.
    (row as unknown as Record<string, unknown>)[rowField] = parsed;
    onChange();
    // Bestand-Cell der Row neu evaluieren — durch die Min/Max-Änderung kann sich
    // `willStockChange` umdrehen und damit die orange Markierung anlegen/entfernen.
    if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true });

    // RPC im Hintergrund, mit beiden Werten (RPC schreibt UPDATE auf beide Spalten).
    const otherValue = (row as unknown as Record<string, unknown>)[otherField] as number | null;
    const minVal = which === 'min' ? parsed : otherValue;
    const maxVal = which === 'max' ? parsed : otherValue;

    void ctx.supabase
      .rpc('kaufland_set_listing_min_max', {
        _storefront: row.storefront,
        _id_unit: row.id_unit,
        _min: minVal,
        _max: maxVal,
      })
      .then((result: { error: { message: string } | null }) => {
        if (result.error) {
          // Revert + User-Hinweis.
          (row as unknown as Record<string, unknown>)[rowField] = oldValue;
          onChange();
          showToast(`Konnte ${which === 'min' ? 'Min' : 'Max'}-Bestand nicht speichern: ${result.error.message}`, 'error');
        }
      });

    return true;
  };
}

function upsertPending(
  pending: Map<string, PendingEdit>,
  row: UnitRow,
  jobField: keyof PendingEdit['fields'],
  value: unknown,
): void {
  const key = `${row.storefront}::${row.id_unit}`;
  const existing = pending.get(key) ?? { storefront: row.storefront, idUnit: row.id_unit, fields: {} };
  (existing.fields as unknown as Record<string, unknown>)[jobField as string] = value;
  pending.set(key, existing);
}

// ---------------------------------------------------------------------------
// Toolbar (Save / Bulk)
// ---------------------------------------------------------------------------

function renderToolbar(
  host: HTMLElement,
  pending: Map<string, PendingEdit>,
  ctx: OffersCtx,
  gridApi: GridApi<UnitRow> | null,
  filters: FilterState,
  onReload: () => void,
): void {
  // Wenn der Tab gerade gewechselt wurde oder der Host noch nicht im DOM hängt,
  // gibt es keinen Toolbar-Container — leise abbrechen statt mit einem
  // "Cannot set properties of null"-Crash zu sterben (Bug-Report 2026-05-20).
  const el = host.querySelector<HTMLDivElement>('#kl-offers-toolbar');
  if (!el) return;
  const selected = gridApi?.getSelectedNodes() ?? [];
  const selCount = selected.length;
  const pendingCount = pending.size;

  // Shipping-Group-Bulk: nur wenn die Selektion komplett aus EINER Storefront ist und der
  // Cache fuer diese Storefront Gruppen enthaelt.
  const selectedStorefronts = new Set<string>();
  for (const n of selected) {
    if (n.data?.storefront) selectedStorefronts.add(n.data.storefront);
  }
  const singleStorefront = selectedStorefronts.size === 1 ? [...selectedStorefronts][0]! : null;
  const gridCtx = (gridApi?.getGridOption('context') ?? null) as OffersGridContext | null;
  const sgCache = singleStorefront ? (gridCtx?.shippingGroups.get(singleStorefront) ?? []) : [];
  const sgBulkEnabled = selCount > 0 && singleStorefront !== null && sgCache.length > 0;
  const sgBulkTooltip = selCount === 0
    ? 'Erst Angebote markieren'
    : singleStorefront === null
      ? 'Bitte nur Angebote einer Storefront markieren'
      : sgCache.length === 0
        ? 'Versandgruppen-Cache leer — bitte erst in den Einstellungen synchronisieren'
        : `Versandgruppe fuer ${selCount} ${selCount === 1 ? 'Angebot' : 'Angebote'} (${singleStorefront.toUpperCase()}) aendern`;

  // Leiste nur rendern, wenn es Inhalt gibt (Auswahl ODER ungespeicherte Änderungen).
  // Im Leerzustand bleibt der Container leer und nimmt keine Höhe ein.
  const hasContent = pendingCount > 0 || selCount > 0;
  el.innerHTML = !hasContent ? '' : `
    <div class="bulkbar">
      ${pendingCount > 0 ? `
        <span class="selinfo"><span class="pd"></span>${pendingCount} ungespeicherte Änderung${pendingCount === 1 ? '' : 'en'}</span>
        <button type="button" class="bulk-btn primary" data-action="save">${KF_ICON.save}Speichern</button>
        <button type="button" class="bulk-btn" data-action="discard">${KF_ICON.undo}Verwerfen</button>
      ` : ''}
      ${pendingCount > 0 && selCount > 0 ? '<span class="bulk-sep"></span>' : ''}
      ${selCount > 0 ? `
        <span class="selinfo"><span class="pd"></span>${selCount} markiert</span>
        <button type="button" class="bulk-btn" data-action="bulk-pause">${KF_ICON.pause}Pausieren</button>
        <button type="button" class="bulk-btn" data-action="bulk-activate">${KF_ICON.play}Aktivieren</button>
        <button type="button" class="bulk-btn" data-action="bulk-shipping-group"
          ${sgBulkEnabled ? '' : 'disabled'}
          title="${escapeHtml(sgBulkTooltip)}">${KF_ICON.truck}Versandgruppe</button>
        <button type="button" class="bulk-btn" data-action="bulk-min-max"
          title="Min-/Max-Bestand für die markierten (zugeordneten) Angebote setzen"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h11M19 7h1M4 17h1M9 17h11"/><circle cx="17" cy="7" r="2"/><circle cx="7" cy="17" r="2"/></svg>Min/Max-Bestand</button>
      ` : ''}
    </div>
  `;

  el.querySelector<HTMLButtonElement>('[data-action="save"]')?.addEventListener('click', () => void savePending(ctx, pending, gridApi, onReload));
  el.querySelector<HTMLButtonElement>('[data-action="discard"]')?.addEventListener('click', () => {
    pending.clear();
    onReload();
  });
  el.querySelector<HTMLButtonElement>('[data-action="bulk-pause"]')?.addEventListener('click', () =>
    void bulkStatus(ctx, gridApi, 'paused', onReload),
  );
  el.querySelector<HTMLButtonElement>('[data-action="bulk-activate"]')?.addEventListener('click', () =>
    void bulkStatus(ctx, gridApi, 'active', onReload),
  );
  el.querySelector<HTMLButtonElement>('[data-action="bulk-shipping-group"]')?.addEventListener('click', () => {
    if (!sgBulkEnabled || !singleStorefront) return;
    const idUnits = selected.map((n) => n.data?.id_unit).filter((u): u is string => Boolean(u));
    void openShippingGroupBulkModal(ctx, singleStorefront, sgCache, idUnits, onReload);
  });
  el.querySelector<HTMLButtonElement>('[data-action="bulk-min-max"]')?.addEventListener('click', () => {
    const rows = selected.map((n) => n.data).filter((d): d is UnitRow => Boolean(d));
    void openMinMaxBulkModal(ctx, rows, gridApi, onReload);
  });
}

async function savePending(
  ctx: OffersCtx,
  pending: Map<string, PendingEdit>,
  gridApi: GridApi<UnitRow> | null,
  onReload: () => void,
): Promise<void> {
  const byStorefront = new Map<string, PendingEdit[]>();
  for (const p of pending.values()) {
    const arr = byStorefront.get(p.storefront) ?? [];
    arr.push(p);
    byStorefront.set(p.storefront, arr);
  }

  try {
    for (const [storefront, edits] of byStorefront) {
      // Chunk into 100s; the RPC validates max 100.
      for (let i = 0; i < edits.length; i += 100) {
        const chunk = edits.slice(i, i + 100).map((e) => ({ idUnit: e.idUnit, ...e.fields }));
        const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_units_update', {
          _storefront: storefront,
          _updates: chunk,
        });
        if (error) throw new Error(error.message);
        const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
        if (!jobId) throw new Error('Kein job_id zurückgeliefert');
        await awaitJob(ctx.supabase, jobId, 60_000);
      }
    }
    pending.clear();
    gridApi?.deselectAll();
    onReload();
  } catch (e) {
    showToast(`Speichern fehlgeschlagen: ${(e as Error).message}`, 'error');
  }
}

async function bulkStatus(
  ctx: OffersCtx,
  gridApi: GridApi<UnitRow> | null,
  status: 'active' | 'paused',
  onReload: () => void,
): Promise<void> {
  const byStorefront = new Map<string, string[]>();
  const selected = gridApi?.getSelectedNodes() ?? [];
  for (const n of selected) {
    const r = n.data;
    if (!r) continue;
    const arr = byStorefront.get(r.storefront) ?? [];
    arr.push(r.id_unit);
    byStorefront.set(r.storefront, arr);
  }
  try {
    for (const [storefront, ids] of byStorefront) {
      for (let i = 0; i < ids.length; i += 100) {
        const chunk = ids.slice(i, i + 100).map((idUnit) => ({ idUnit, status }));
        const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_units_update', {
          _storefront: storefront,
          _updates: chunk,
        });
        if (error) throw new Error(error.message);
        const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
        if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      }
    }
    gridApi?.deselectAll();
    onReload();
  } catch (e) {
    showToast(`Bulk-Aktion fehlgeschlagen: ${(e as Error).message}`, 'error');
  }
}

// ---------------------------------------------------------------------------
// Cell renderers / helpers
// ---------------------------------------------------------------------------

function storefrontRenderer(p: ICellRendererParams<UnitRow>): string {
  const sf = (p.value as string | null) ?? '';
  if (!sf) return '';
  // Inline-SVG-Flagge (Emoji-Flaggen rendern auf Windows nur als Buchstaben-Box);
  // Code als title für Screenreader/Hover. Fallback (unbekanntes Land) = neutrales Grau.
  return `<span class="kl-sf-cell" title="${sf.toUpperCase()}"><span class="flag">${kfFlagSvg(sf)}</span></span>`;
}

function varioStockFormatter(p: ValueFormatterParams<UnitRow>): string {
  // Kein Mapping → leer; bestandsloser Artikel → „o. B."; gemappt aber noch kein
  // Refresh → '…'; sonst die Zahl.
  //
  // „o. B." statt „kein Bestand": Die Spalte ist 68 px breit und rechtsbuendig, der lange Text
  // sprengte sie. Ausgeschrieben steht es im Spalten-Tooltip und im Detail-Drawer, wo Platz ist.
  if (!p.data?.vario_article_id) return '';
  if (isStocklessRow(p.data)) return 'o. B.';
  if (p.value === null || p.value === undefined) return '…';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(p.value as number);
}

function statusRenderer(p: ICellRendererParams<UnitRow>) {
  if (p.data?.deleted_at) return `<span class="st st-inaktiv"><span class="pd"></span>Gelöscht</span>`;
  const status = (p.value as string | null) ?? 'unknown';
  const cls = status === 'active' ? 'st-aktiv' : status === 'paused' ? 'st-paus' : 'st-inaktiv';
  return `<span class="st ${cls}"><span class="pd"></span>${STATUS_LABEL[status] ?? status}</span>`;
}

/**
 * Kombinierter Actions-Renderer: drei Icons in einer Zelle — Frontend-Shop, Seller-Portal
 * (beide externe Links) und Vario-Artikel-Zuweisen (öffnet Dialog). Spart Platz gegenüber den
 * früheren drei separaten Spalten.
 */
function actionsRenderer(p: ICellRendererParams<UnitRow>, ctx: OffersCtx): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'act-cell';
  wrap.style.justifyContent = 'center';

  // 1) Frontend-Shop-Link.
  const frontendUrl = p.data ? kauflandFrontendUrl(p.data.storefront, p.data.id_product) : null;
  if (frontendUrl) {
    const a = document.createElement('a');
    a.className = 'act-icon';
    a.href = frontendUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    a.title = 'Kaufland-Shop öffnen';
    a.innerHTML = '<span class="v-icon fa-regular fa-bag-shopping"></span>';
    wrap.appendChild(a);
  } else {
    wrap.appendChild(makeDisabledIcon('fa-bag-shopping', 'Keine Produkt-ID — Shop nicht verlinkbar'));
  }

  // 2) Seller-Portal-Link.
  const backendUrl = p.data ? kauflandBackendUrl(p.data.storefront, p.data.id_unit, p.data.id_offer) : null;
  if (backendUrl) {
    const a = document.createElement('a');
    a.className = 'act-icon';
    a.href = backendUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    a.title = 'Im Seller-Portal öffnen';
    a.innerHTML = '<span class="v-icon fa-regular fa-gear"></span>';
    wrap.appendChild(a);
  } else {
    wrap.appendChild(makeDisabledIcon('fa-gear', 'Seller-Portal nicht verlinkbar'));
  }

  // 3) Vario-Artikel im neuen Tab öffnen — nur sichtbar wenn zugeordnet.
  if (p.data?.vario_article_id) {
    const varioUrl = varioArticleUrl(p.data.vario_article_id);
    if (varioUrl) {
      const a = document.createElement('a');
      a.className = 'act-icon';
      a.href = varioUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.title = `Vario-Artikel ${p.data.vario_article_number ?? p.data.vario_article_id} öffnen`;
      a.innerHTML = '<span class="v-icon fa-regular fa-up-right-from-square"></span>';
      wrap.appendChild(a);
    }
  }

  // 4) Vario-Article-Zuweisen / Neu-Anlegen — Dialog. Icon wechselt je nach Mapping-Status:
  //    "fa-link" wenn noch nicht zugeordnet, "fa-pen" wenn schon (= Mapping ändern).
  // Bewusst dieselbe `.kl-link-icon`-Klasse wie die externen Links, damit alle 3/4
  // Aktions-Buttons in einer Reihe gleich groß + Vario-grün aussehen.
  const linkBtn = document.createElement('button');
  linkBtn.type = 'button';
  linkBtn.className = 'act-icon';
  const isMapped = !!p.data?.vario_article_id;
  linkBtn.title = isMapped ? 'Vario-Zuordnung ändern' : 'Vario-Artikel zuweisen oder anlegen';
  linkBtn.innerHTML = `<span class="v-icon fa-regular ${isMapped ? 'fa-pen' : 'fa-link'}"></span>`;
  linkBtn.addEventListener('click', () => {
    if (!p.data) return;
    openArticleDialog({
      supabase: ctx.supabase,
      supabaseUrl: ctx.supabaseUrl,
      session: ctx.session,
      unit: {
        storefront: p.data.storefront,
        idUnit: p.data.id_unit,
        ean: p.data.ean,
        sku: p.data.id_offer,
        title: p.data.title,
        varioArticleId: p.data.vario_article_id,
        varioArticleNumber: p.data.vario_article_number,
      },
      // Mapping kann sich geändert haben (Assign/Unassign) → ganzes Grid neu laden, nicht nur
      // die eine Zeile, weil der Mapping-Filter im Toolbar oben evtl. die Zeile ausblenden soll.
      onDone: () => {
        p.api?.purgeInfiniteCache();
      },
    });
  });
  wrap.appendChild(linkBtn);

  return wrap;
}

function makeDisabledIcon(faClass: string, tooltip: string): HTMLElement {
  const span = document.createElement('span');
  span.className = 'act-icon kl-link-placeholder';
  span.title = tooltip;
  span.innerHTML = `<span class="v-icon fa-regular ${faClass}" style="opacity:0.35;"></span>`;
  return span;
}

function centsToEur(c: number | null): number | null {
  return c === null ? null : c / 100;
}

function eurFormatter(p: ValueFormatterParams<UnitRow>): string {
  if (p.value === null || p.value === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(p.value as number);
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'gerade eben';
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} h`;
  const days = Math.round(h / 24);
  if (days < 30) return `vor ${days} T`;
  return d.toLocaleDateString('de-DE');
}

// ---------------------------------------------------------------------------
// Data loaders
// ---------------------------------------------------------------------------

async function loadSummary(ctx: OffersCtx): Promise<SummaryRow[]> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('units_summary')
    .select('tenant_id,storefront,status,alive,tombstoned,total');
  if (error) {
    console.error('[offers] loadSummary', error);
    return [];
  }
  return (data ?? []) as SummaryRow[];
}

/** PostgREST column names that AG Grid is allowed to sort by. */
const SORTABLE_COLUMNS: Record<string, string> = {
  storefront: 'storefront',
  id_offer: 'id_offer',
  vario_article_number: 'vario_article_number',
  ean: 'ean',
  title: 'title',
  status: 'status',
  price_cents: 'price_cents',
  stock: 'stock',
  vario_stock: 'vario_stock',
  minimum_price_cents: 'minimum_price_cents',
  handling_time: 'handling_time',
  updated_at: 'updated_at',
  fetched_at: 'fetched_at',
  vat_indicator: 'vat_indicator',
};

interface PageRequest {
  startRow: number;
  endRow: number;
  sortModel: ReadonlyArray<{ colId: string; sort: 'asc' | 'desc' }>;
}

/**
 * Im AG-Grid-`context` referenziert, damit `SelectAllHeader` (Klasse außerhalb der
 * Closure von `renderOffersTab`) die Toolbar nach Select-All-Toggle neu rendern kann.
 */
interface OffersGridContext {
  refreshToolbar: () => void;
  /** Pro storefront die verfuegbaren Versandgruppen. Wird beim Tab-Init aus kaufland.shipping_groups
   *  geladen und vom CellEditor + CellRenderer der "Versandgruppe"-Spalte gelesen. */
  shippingGroups: Map<string, ShippingGroupRow[]>;
  /** Pro storefront das Mapping `vat_indicator` (z.B. "standard_rate") → `vat_rate` (Prozent
   *  als number, z.B. 19). Aus `kaufland.vat_indicators` (global, storefront-gebunden) — wird
   *  beim Tab-Init geladen und vom Renderer der "MwSt"-Spalte gelesen. */
  vatIndicators: Map<string, Map<string, number | null>>;
}

/**
 * WHERE-Predicates für `loadUnitsPage` (Grid-Datasource). Search-Filter inkludiert —
 * Mehrfach-Token mit AND-stacked OR-Conditions.
 *
 * Type-loose Rückgabe: PostgrestFilterBuilder ist generisch über Tabellen/Schemas, die
 * konkreten Type-Args ändern sich pro Caller — wir lassen `unknown`/`any` für die Builder-
 * Typen und vertrauen auf die Caller, dass sie das Query-Objekt korrekt weiterverwenden.
 */
function applyOffersFilters<T extends {
  eq: (col: string, val: unknown) => T;
  is: (col: string, val: unknown) => T;
  not: (col: string, op: string, val: unknown) => T;
  or: (cond: string) => T;
}>(q: T, filters: FilterState): T {
  if (filters.storefront) q = q.eq('storefront', filters.storefront);
  if (filters.status === 'deleted') q = q.not('deleted_at', 'is', null);
  else if (filters.status) q = q.is('deleted_at', null).eq('status', filters.status);
  if (filters.mapping === 'mapped') q = q.not('vario_article_id', 'is', null);
  else if (filters.mapping === 'unmapped') q = q.is('vario_article_id', null);
  else if (filters.mapping === 'exists-unmapped')
    q = q.is('vario_article_id', null).not('suggested_vario_article_id', 'is', null);

  if (filters.search.trim()) {
    // PostgREST .or() trennt Conditions per Komma — Sonderzeichen im User-Input (Komma,
    // Klammern, Backslash) würden den Filter-String zerbrechen und unsinnige Ergebnisse
    // liefern (Bug-Report 2026-05-18: lange Suchbegriffe mit Komma → leere Zeilen).
    // Strategie: SQL-Wildcards (%/_) escapen, alle or()-Separatoren in Whitespace umwandeln,
    // dann in Tokens splitten und pro Token eine OR-Bedingung über die 4 Spalten AND-stacken.
    // Tokens < 2 Zeichen fliegen raus (Stoppwörter, Einzelbuchstaben).
    const sanitized = filters.search
      .trim()
      .replace(/[%_]/g, (m) => `\\${m}`)
      .replace(/[,()\\]/g, ' ');
    const tokens = sanitized.split(/\s+/).filter((t) => t.length >= 2);
    for (const tok of tokens) {
      const term = `%${tok}%`;
      q = q.or(
        `ean.ilike.${term},title.ilike.${term},id_unit.ilike.${term},id_offer.ilike.${term},vario_article_number.ilike.${term}`,
      );
    }
  }
  return q;
}

/**
 * Server-side page fetch for the Infinite Row Model. Uses PostgREST `.range(start, end-1)`
 * and `{ count: 'exact' }` so the grid can size the virtual scrollbar correctly. Sorts are
 * derived from AG Grid's sortModel (whitelisted via SORTABLE_COLUMNS to avoid arbitrary
 * field injection). Filters come from our own toolbar (storefront/status/search) — column-
 * level filters are intentionally disabled in defaultColDef to keep the query surface small.
 */
async function loadUnitsPage(
  ctx: OffersCtx,
  filters: FilterState,
  page: PageRequest,
): Promise<{ rows: UnitRow[]; total: number | null }> {
  let q = ctx.supabase
    .schema('kaufland')
    .from('units_with_mapping')
    .select(
      'storefront,id_unit,id_offer,id_product,ean,title,status,price_cents,stock,minimum_price_cents,handling_time,updated_at,fetched_at,deleted_at,vario_article_id,vario_article_number,vario_stock,vario_stock_fetched_at,min_bestand,max_bestand,suggested_vario_article_id,suggested_vario_article_number,id_shipping_group,vat_indicator,vario_product_type',
      { count: 'exact' },
    );
  q = applyOffersFilters(q, filters);

  // Apply AG Grid sort model (max 2 levels — more is overkill for this UI).
  // Default beim Tab-Öffnen: SKU desc. Greift, wenn AG Grid (noch) keinen
  // sortModel-Eintrag liefert — z. B. beim allerersten Datasource-Call.
  const sorts = page.sortModel.length > 0 ? page.sortModel.slice(0, 2) : [{ colId: 'id_offer', sort: 'desc' as const }];
  let anySort = false;
  for (const s of sorts) {
    const col = SORTABLE_COLUMNS[s.colId];
    if (!col) continue;
    q = q.order(col, { ascending: s.sort === 'asc', nullsFirst: false });
    anySort = true;
  }
  if (!anySort) q = q.order('id_offer', { ascending: false });

  q = q.range(page.startRow, page.endRow - 1);

  const { data, error, count } = await q;
  if (error) {
    console.error('[offers] loadUnitsPage', error);
    throw new Error(error.message);
  }
  return { rows: (data ?? []) as UnitRow[], total: count ?? null };
}

// ---------------------------------------------------------------------------
// Storefront-filter dropdown
// ---------------------------------------------------------------------------

function populateStorefrontFilter(
  host: HTMLElement,
  summary: SummaryRow[],
  activated: ActivatedStorefront[],
  filters: { storefront: string | null },
) {
  const sel = host.querySelector<HTMLSelectElement>('#kl-offers-storefront')!;
  // Aktivierte Storefronts MIT den in den Daten vorkommenden vereinigen: sonst fehlt ein frisch
  // freigeschalteter Marktplatz, solange er kein Angebot hat (so verschwand NL am 2026-08-09).
  const storefronts = storefrontFilterOptions(activated, summary.map((s) => s.storefront));
  sel.innerHTML = `
    <option value="">Alle Storefronts</option>
    ${storefronts.map((sf) => `<option value="${sf}" ${filters.storefront === sf ? 'selected' : ''}>${sf.toUpperCase()}</option>`).join('')}
  `;
}

// ---------------------------------------------------------------------------
// Custom header component: „Alle Zeilen der aktuellen Page markieren"
// ---------------------------------------------------------------------------

/**
 * AG-Grid v32 unterstützt `rowSelection.headerCheckbox` beim Infinite Row Model nicht
 * (siehe `selectAllFeature.checkRightRowModelType`). Diese Custom-Component übernimmt die
 * Rolle: Klick markiert genau die Rows der aktuell sichtbaren Page (max
 * `paginationPageSize`, default 50 — vom User per Footer-Selector auf bis zu 200
 * erhöhbar). Bewusst kein Cross-Page-Select-All — Bulk-Aktionen sollen nicht
 * versehentlich 3000 Treffer in einem Rutsch verändern; der User markiert page-weise.
 */
class SelectAllHeader implements IHeaderComp {
  private eGui!: HTMLDivElement;
  private eWrapper!: HTMLDivElement;
  private input!: HTMLInputElement;
  private api!: GridApi;
  private offersCtx!: OffersGridContext;
  private onSelectionChanged = () => this.syncState();
  private onModelUpdated = () => this.onCacheUpdated();

  init(params: IHeaderParams): void {
    this.api = params.api;
    this.offersCtx = params.context as OffersGridContext;
    // DOM 1:1 wie AG-Grids interne AgCheckbox-Komponente, damit das Quartz-Theme die Checkbox
    // exakt wie die Row-Checkboxen darunter rendert. State-Klassen `ag-checked` und
    // `ag-indeterminate` werden in `syncState` auf den Wrapper toggled — exakt wie AG-Grid
    // es in `setValue` macht.
    this.eGui = document.createElement('div');
    this.eGui.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;width:100%;';
    this.eGui.innerHTML = `
      <div class="ag-labeled ag-label-align-right ag-checkbox ag-input-field" role="presentation">
        <div class="ag-input-field-label ag-label ag-hidden ag-checkbox-label" role="presentation"></div>
        <div class="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper" role="presentation">
          <input class="ag-input-field-input ag-checkbox-input" type="checkbox"
                 aria-label="Alle gefilterten Zeilen markieren" />
        </div>
      </div>`;
    this.eWrapper = this.eGui.querySelector<HTMLDivElement>('.ag-checkbox-input-wrapper')!;
    this.input = this.eGui.querySelector<HTMLInputElement>('input')!;
    this.input.addEventListener('change', () => void this.toggleAll());
    // Klick darf nicht die Header-Sortierung triggern (passiert bei sortable=false ohnehin nicht,
    // aber stopPropagation ist Defense-in-Depth gegen zukünftige Änderungen).
    this.input.addEventListener('click', (e) => e.stopPropagation());
    this.api.addEventListener('selectionChanged', this.onSelectionChanged);
    this.api.addEventListener('modelUpdated', this.onModelUpdated);
    this.syncState();
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return true;
  }

  destroy(): void {
    this.api.removeEventListener('selectionChanged', this.onSelectionChanged);
    this.api.removeEventListener('modelUpdated', this.onModelUpdated);
  }

  /**
   * Klick auf die Header-Checkbox:
   *   - Checked → markiert alle Rows der aktuell sichtbaren Page (max
   *     `paginationPageSize`, default 50). Bewusst kein Cross-Page-Select-All —
   *     der User soll nicht versehentlich 3000 Treffer in einer Bulk-Aktion
   *     verschieben.
   *   - Unchecked → Grid-Deselect.
   */
  private toggleAll(): void {
    if (!this.input.checked) {
      this.api.deselectAll();
      this.offersCtx.refreshToolbar();
      return;
    }
    // Erst Cross-Page-Selektionen aus dem Cache räumen, damit „Alle markieren"
    // ausschließlich die aktuell sichtbare Page betrifft. Sonst akkumulieren sich
    // Selektionen aus vorherigen Pages — der Counter zeigt dann z. B. 87 obwohl
    // visuell nur 50 markiert wirken.
    this.api.deselectAll();
    const pageSize = this.api.paginationGetPageSize();
    const pageStart = this.api.paginationGetCurrentPage() * pageSize;
    const pageEnd = pageStart + pageSize;
    this.api.forEachNode((node: IRowNode) => {
      if (!node.data || node.rowIndex == null) return;
      if (node.rowIndex >= pageStart && node.rowIndex < pageEnd) {
        node.setSelected(true, false, 'uiSelectAll');
      }
    });
    this.offersCtx.refreshToolbar();
    this.syncState();
  }

  /**
   * Wird bei jedem Cache-Update (neue Block geladen / Scroll / purge) aufgerufen, damit
   * die Checkbox-Optik (checked / indeterminate / leer) synchron bleibt.
   */
  private onCacheUpdated(): void {
    this.syncState();
  }

  /**
   * Spiegelt den State der Checkbox: leitet aus der Page-Selection ab (alle / einige /
   * keine der Rows auf der aktuellen Page selektiert).
   */
  private syncState(): void {
    const pageSize = this.api.paginationGetPageSize();
    const pageStart = this.api.paginationGetCurrentPage() * pageSize;
    const pageEnd = pageStart + pageSize;
    let total = 0;
    let selected = 0;
    this.api.forEachNode((node: IRowNode) => {
      if (!node.data || node.rowIndex == null) return;
      if (node.rowIndex < pageStart || node.rowIndex >= pageEnd) return;
      total += 1;
      if (node.isSelected()) selected += 1;
    });
    const allChecked = total > 0 && selected === total;
    const indeterminate = selected > 0 && selected < total;
    this.input.checked = allChecked;
    this.input.indeterminate = indeterminate;
    this.eWrapper.classList.toggle('ag-checked', allChecked);
    this.eWrapper.classList.toggle('ag-indeterminate', indeterminate);
  }
}

// ---------------------------------------------------------------------------
// Shipping-Groups: Cache + Cell-Renderer + Custom Cell-Editor
// ---------------------------------------------------------------------------

async function loadShippingGroups(ctx: OffersCtx, gridContext: OffersGridContext): Promise<void> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('shipping_groups')
    .select('storefront,id_shipping_group,name')
    .order('storefront')
    .order('name');
  if (error) {
    console.warn('[offers] loadShippingGroups failed', error.message);
    return;
  }
  gridContext.shippingGroups.clear();
  for (const r of (data ?? []) as Array<{ storefront: string; id_shipping_group: string; name: string | null }>) {
    const list = gridContext.shippingGroups.get(r.storefront) ?? [];
    list.push({ id_shipping_group: r.id_shipping_group, name: r.name });
    gridContext.shippingGroups.set(r.storefront, list);
  }
}

/**
 * Lädt das globale (kein tenant-bezug) `kaufland.vat_indicators`-Cache und baut daraus
 * pro Storefront ein Mapping `vat_indicator → vat_rate (Prozent)`. Wird vom Renderer
 * der "MwSt"-Spalte genutzt, um den Kaufland-Code (`standard_rate`) in eine lesbare
 * Prozentangabe (`19 %`) umzuwandeln. Tabelle hat seit Migration 0094 einen Sonntags-Cron;
 * im Worst Case ist der Cache leer und wir zeigen den rohen Code.
 */
async function loadVatIndicators(ctx: OffersCtx, gridContext: OffersGridContext): Promise<void> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('vat_indicators')
    .select('storefront,vat_indicator,vat_rate');
  if (error) {
    console.warn('[offers] loadVatIndicators failed', error.message);
    return;
  }
  gridContext.vatIndicators.clear();
  for (const r of (data ?? []) as Array<{ storefront: string; vat_indicator: string; vat_rate: string | number | null }>) {
    const map = gridContext.vatIndicators.get(r.storefront) ?? new Map<string, number | null>();
    const rate = typeof r.vat_rate === 'number'
      ? r.vat_rate
      : typeof r.vat_rate === 'string' && r.vat_rate.trim() !== ''
        ? Number(r.vat_rate)
        : null;
    map.set(r.vat_indicator, rate !== null && Number.isFinite(rate) ? rate : null);
    gridContext.vatIndicators.set(r.storefront, map);
  }
}

function escapeHtml(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function openShippingGroupBulkModal(
  ctx: OffersCtx,
  storefront: string,
  groups: ShippingGroupRow[],
  idUnits: string[],
  onReload: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    const body = `
      <div class="kl-modal-meta">
        <strong>${idUnits.length} ${idUnits.length === 1 ? 'Angebot' : 'Angebote'}</strong>
        <div class="text-grey">Storefront <strong>${escapeHtml(storefront.toUpperCase())}</strong></div>
      </div>
      <div class="kl-form-row">
        <label for="kl-sg-pick">Neue Versandgruppe</label>
        <div class="v-input kl-select-input" style="width:100%;">
          <select id="kl-sg-pick">
            <option value="">Bitte wählen …</option>
            ${groups.map((g) => `<option value="${escapeHtml(g.id_shipping_group)}">${escapeHtml(g.name ?? g.id_shipping_group)}</option>`).join('')}
          </select>
          <span class="v-icon fa-light fa-chevron-down"></span>
        </div>
      </div>
    `;
    const footer = `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button--primary" data-action="apply" disabled>
        <span class="v-icon fa-regular fa-check"></span>Übernehmen
      </button>
    `;

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const modal = openModal({
      title: 'Versandgruppe ändern',
      icon: 'fa-truck',
      width: 520,
      body,
      footer,
      onClose: finish,
    });

    const sel = modal.el.querySelector<HTMLSelectElement>('#kl-sg-pick')!;
    const applyBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="apply"]')!;
    sel.addEventListener('change', () => { applyBtn.disabled = !sel.value; });
    applyBtn.addEventListener('click', async () => {
      const idShippingGroup = sel.value;
      if (!idShippingGroup) return;
      applyBtn.disabled = true;
      applyBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>Übernehme …';
      await applyShippingGroupChange(ctx, storefront, idShippingGroup, idUnits);
      onReload();
      modal.close();
    });
    setTimeout(() => sel.focus(), 50);
  });
}

async function applyShippingGroupChange(
  ctx: OffersCtx,
  storefront: string,
  idShippingGroup: string,
  idUnits: string[],
): Promise<void> {
  const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_set_shipping_group', {
    _storefront: storefront,
    _id_shipping_group: idShippingGroup,
    _id_units: idUnits,
  });
  if (error) {
    showToast(`Versandgruppe konnte nicht gesetzt werden: ${error.message}`, 'error');
    return;
  }
  const row = Array.isArray(data) ? data[0] : data;
  const jobId = (row as { job_id?: string } | null)?.job_id;
  if (!jobId) {
    showToast('Job konnte nicht angelegt werden.', 'error');
    return;
  }
  try {
    const result = await awaitJob(ctx.supabase, jobId, 60_000);
    const r = (result?.result ?? {}) as { updated?: number; failed?: Array<{ idUnit: string; error: string }> };
    const updated = r.updated ?? 0;
    const failedCount = r.failed?.length ?? 0;
    if (failedCount === 0) {
      showToast(
        idUnits.length === 1
          ? 'Versandgruppe aktualisiert.'
          : `${updated} von ${idUnits.length} Angeboten aktualisiert.`,
        'success',
      );
    } else {
      showToast(
        `${updated} aktualisiert · ${failedCount} fehlgeschlagen (Konsole für Details).`,
        failedCount === idUnits.length ? 'error' : 'info',
      );
      console.warn('[offers] set_shipping_group failed units:', r.failed);
    }
  } catch (e) {
    showToast(`Sync fehlgeschlagen: ${(e as Error).message}`, 'error');
  }
}

/**
 * Bulk-Setter für Min-/Max-Bestand der markierten Angebote. Ein Modal mit zwei
 * Zahlenfeldern (Min, Max) — leeres Feld = diese Spalte unverändert lassen. Min/Max
 * sind reine Vario-Settings (vario.article_mappings) und existieren NUR für ZUGEORDNETE
 * Listings; nicht zugeordnete Markierungen haben kein Mapping und werden übersprungen.
 * KEIN Kaufland-Push — der Worker `kaufland.stock_sync` wertet die Werte aus.
 */
function openMinMaxBulkModal(
  ctx: OffersCtx,
  rows: UnitRow[],
  gridApi: GridApi<UnitRow> | null,
  onReload: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    const total = rows.length;
    const mapped = rows.filter((r) => r.vario_article_id != null);
    const skipped = total - mapped.length;

    const body = `
      <div class="kl-modal-meta">
        <strong>${total} ${total === 1 ? 'Angebot' : 'Angebote'}</strong>
        <div class="text-grey">${mapped.length} zugeordnet${skipped > 0 ? ` · ${skipped} nicht zugeordnet (werden übersprungen)` : ''}</div>
      </div>
      <div class="kl-form-row">
        <label for="kl-mm-min">Min-Bestand</label>
        <div class="v-input"><input id="kl-mm-min" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
      </div>
      <div class="kl-form-row">
        <label for="kl-mm-max">Max-Bestand</label>
        <div class="v-input"><input id="kl-mm-max" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
      </div>
      <div class="kl-form-hint">Leeres Feld bleibt unverändert. Min/Max sind Vario-Einstellungen (kein Kaufland-Push) — der Bestands-Sync wertet sie aus.</div>
      <div class="kl-modal-error" id="kl-mm-error" hidden></div>
    `;
    const footer = `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button--primary" data-action="apply" disabled>
        <span class="v-icon fa-regular fa-check"></span>Übernehmen
      </button>
    `;

    let settled = false;
    const finish = () => { if (settled) return; settled = true; resolve(); };

    const modal = openModal({
      title: 'Min/Max-Bestand',
      icon: 'fa-layer-group',
      width: 460,
      body,
      footer,
      onClose: finish,
    });

    const minInp = modal.el.querySelector<HTMLInputElement>('#kl-mm-min')!;
    const maxInp = modal.el.querySelector<HTMLInputElement>('#kl-mm-max')!;
    const errEl = modal.el.querySelector<HTMLDivElement>('#kl-mm-error')!;
    const applyBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="apply"]')!;

    // '' → null (Spalte lassen); gültige Ganzzahl ≥ 0 → Zahl; sonst undefined (ungültig).
    const parse = (v: string): number | null | undefined => {
      const t = v.trim();
      if (t === '') return null;
      const n = Number.parseInt(t, 10);
      if (Number.isNaN(n) || n < 0) return undefined;
      return n;
    };

    const validate = () => {
      const min = parse(minInp.value);
      const max = parse(maxInp.value);
      let error: string | null = null;
      if (mapped.length === 0) {
        error = 'Keines der markierten Angebote ist zugeordnet — Min/Max gibt es nur für zugeordnete Listings.';
      } else if (min === undefined || max === undefined) {
        error = 'Bitte ganze Zahlen ≥ 0 eingeben.';
      } else if (min !== null && max !== null && min > max) {
        error = `Min (${min}) darf nicht größer als Max (${max}) sein.`;
      }
      const valuesOk = min !== undefined && max !== undefined && !(min === null && max === null);
      applyBtn.disabled = !(error === null && valuesOk);
      errEl.hidden = error === null;
      if (error) errEl.textContent = error;
    };
    minInp.addEventListener('input', validate);
    maxInp.addEventListener('input', validate);
    validate(); // Initialzustand (zeigt z.B. sofort den „nichts zugeordnet"-Hinweis)

    applyBtn.addEventListener('click', async () => {
      const min = parse(minInp.value);
      const max = parse(maxInp.value);
      if (min === undefined || max === undefined || (min === null && max === null)) return;
      applyBtn.disabled = true;
      applyBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>Übernehme …';
      const updated = await applyMinMaxBulk(ctx, mapped, min, max);
      gridApi?.deselectAll();
      onReload();
      if (updated > 0) {
        const parts: string[] = [];
        if (min !== null) parts.push(`Min ${min}`);
        if (max !== null) parts.push(`Max ${max}`);
        showToast(
          `${parts.join(' · ')} für ${updated} ${updated === 1 ? 'Angebot' : 'Angebote'} gesetzt${skipped > 0 ? ` · ${skipped} übersprungen` : ''}.`,
          'success',
        );
      }
      modal.close();
    });

    setTimeout(() => minInp.focus(), 50);
  });
}

/**
 * Schreibt Min/Max der (gemappten) Rows per Storefront-Gruppe über die Bulk-RPC
 * `kaufland_bulk_set_listing_min_max` (COALESCE-Semantik: null = Spalte unverändert).
 * Gibt die Gesamtzahl aktualisierter Mappings zurück.
 */
async function applyMinMaxBulk(
  ctx: OffersCtx,
  rows: UnitRow[],
  min: number | null,
  max: number | null,
): Promise<number> {
  const byStorefront = new Map<string, string[]>();
  for (const r of rows) {
    const arr = byStorefront.get(r.storefront) ?? [];
    arr.push(r.id_unit);
    byStorefront.set(r.storefront, arr);
  }
  let updated = 0;
  try {
    for (const [storefront, idUnits] of byStorefront) {
      const { data, error } = await ctx.supabase.rpc('kaufland_bulk_set_listing_min_max', {
        _storefront: storefront,
        _id_units: idUnits,
        _min: min,
        _max: max,
      });
      if (error) throw new Error(error.message);
      updated += typeof data === 'number' ? data : Number(data ?? 0);
    }
  } catch (e) {
    showToast(`Min/Max konnte nicht gesetzt werden: ${(e as Error).message}`, 'error');
  }
  return updated;
}
